import { useCallback, useEffect, useMemo, useState } from "react";
import {
  type AfirmacionVisor,
  type ClienteNexo,
  type DecisionMencion,
  type DecisionRevision,
  type EstadoAfirmacionVisor,
  type FilaEntidad,
  type MencionVisor,
  type VisorDocumento,
  justiciaRevisarAfirmacion,
  justiciaRevisarResolucion,
  vizEntidades,
  visorCrearAfirmacion,
  visorCrearMencion,
  visorDocumento,
  visorRevisarMencion,
  visorSnapshotUrl,
  visorVincularMencion,
} from "../api.ts";
import { C, CLAIM_STATUS_ES, MENCION_TIPO_ES, fecha, schemaEs, st } from "../theme.ts";
import { BotonesRevision, Cargando, ErrorBox } from "../ui/componentes.tsx";
import {
  type FiltrosVisor,
  type ResaltadoActivo,
  type SeleccionAnotada,
  TextoAnotado,
  colorMencionTipo,
  leerSeleccionAnotada,
} from "../ui/TextoAnotado.tsx";
import { rutaAHash } from "../rutas.ts";

// Vocabulario de postura del CHECK de nexo.afirmacion (0094 §1.8); solo se usa
// para mostrar una etiqueta legible, la validación real vive en la RPC.
const POSTURA_ES: Record<string, string> = {
  allegation: "Alegación",
  admission: "Admisión",
  verdict: "Veredicto",
  order: "Orden",
  administrative_record: "Registro administrativo",
  procedural: "Procesal",
};

const TIPOS_MENCION = Object.keys(MENCION_TIPO_ES);
const ESTADOS_AFIRMACION: EstadoAfirmacionVisor[] = ["proposed", "approved", "rejected"];
// Techo probatorio para creación manual (§1.8 del diseño): 'sanctioned' y
// 'adjudicated' quedan fuera a propósito, el server los rechazaría.
const CLAIM_STATUS_CREACION = ["mentioned", "reported", "alleged", "investigated", "denied", "unclear"] as const;

type PopoverEstado =
  | { tipo: "mencion"; sel: SeleccionAnotada }
  | { tipo: "afirmacion"; sel: SeleccionAnotada };

/**
 * RevisarDocumento — visor de revisión documental (§3 del diseño): el texto
 * completo del documento con menciones/afirmaciones resaltadas encima,
 * revisión en dos pasos, vinculación a entidades y creación manual por
 * selección de texto. Nada aquí muta registros del extractor: todo pasa por
 * las RPCs de revisión/creación, que dejan su propio rastro de autoría.
 */
export function RevisarDocumento({ cliente, id }: { cliente: ClienteNexo; id: string }) {
  const [doc, setDoc] = useState<VisorDocumento | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [errorRevision, setErrorRevision] = useState<string | null>(null);

  const [tab, setTab] = useState<"pendientes" | "menciones" | "afirmaciones">("pendientes");
  const [seleccion, setSeleccion] = useState<ResaltadoActivo | null>(null);

  const [tiposVisibles, setTiposVisibles] = useState<Set<string>>(() => new Set(TIPOS_MENCION));
  const [estadosAfirmacionVisibles, setEstadosAfirmacionVisibles] = useState<Set<EstadoAfirmacionVisor>>(
    () => new Set(ESTADOS_AFIRMACION),
  );
  const [verRechazadas, setVerRechazadas] = useState(false);
  const [soloPendientes, setSoloPendientes] = useState(false);

  const [selActiva, setSelActiva] = useState<SeleccionAnotada | null>(null);
  const [popover, setPopover] = useState<PopoverEstado | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    setDoc(undefined);
    setError(null);
    visorDocumento(cliente, id)
      .then((d) => {
        if (vivo) setDoc(d);
      })
      .catch((e: unknown) => {
        if (vivo) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      vivo = false;
    };
  }, [cliente, id]);

  function mostrarToast(msg: string): void {
    setToast(msg);
    window.setTimeout(() => { setToast(null); }, 3500);
  }

  function alternarTipo(t: string): void {
    setTiposVisibles((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t); else next.add(t);
      return next;
    });
  }

  function alternarEstado(e: EstadoAfirmacionVisor): void {
    setEstadosAfirmacionVisibles((prev) => {
      const next = new Set(prev);
      if (next.has(e)) next.delete(e); else next.add(e);
      return next;
    });
  }

  function cerrarPopover(): void {
    setPopover(null);
    setSelActiva(null);
    window.getSelection()?.removeAllRanges();
  }

  function manejarMouseUp(): void {
    const resultado = leerSeleccionAnotada();
    if (resultado === null) {
      setSelActiva(null);
      return;
    }
    if (resultado === "cruzada") {
      setSelActiva(null);
      mostrarToast("Selecciona dentro de un mismo párrafo.");
      return;
    }
    setSelActiva(resultado);
  }

  function irATextoMencion(m: MencionVisor): void {
    setSeleccion({ tipo: "mencion", id: m.id });
    if (!tiposVisibles.has(m.mention_type)) alternarTipo(m.mention_type);
    if (m.revision?.decision === "rejected" && !verRechazadas) setVerRechazadas(true);
    if (soloPendientes && m.revision !== null) setSoloPendientes(false);
    requestAnimationFrame(() => {
      document.querySelector(`span[data-mencion-ids~="${String(m.id)}"]`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }

  function irATextoAfirmacion(a: AfirmacionVisor): void {
    setSeleccion({ tipo: "afirmacion", id: a.id });
    if (!estadosAfirmacionVisibles.has(a.estado)) alternarEstado(a.estado);
    if (soloPendientes && a.estado !== "proposed") setSoloPendientes(false);
    requestAnimationFrame(() => {
      document.querySelector(`span[data-afirmacion-ids~="${a.id}"]`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }

  // useCallback: son props de TextoAnotado (memoizado) — identidad estable
  // para que los fragmentos no re-rendereen con cada cambio de estado global.
  const irACardMencion = useCallback((mencionId: number): void => {
    setSeleccion({ tipo: "mencion", id: mencionId });
    setTab("menciones");
    requestAnimationFrame(() => {
      document.getElementById(`mencion-${String(mencionId)}`)?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
  }, []);

  const irACardAfirmacion = useCallback((afirmacionId: string): void => {
    setSeleccion({ tipo: "afirmacion", id: afirmacionId });
    setTab("afirmaciones");
    requestAnimationFrame(() => {
      document.getElementById(`afirmacion-${afirmacionId}`)?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
  }, []);

  async function revisarMencion(mencionId: number, decision: DecisionMencion): Promise<void> {
    setErrorRevision(null);
    try {
      const r = await visorRevisarMencion(cliente, mencionId, decision);
      setDoc((d) => d
        ? { ...d, menciones: d.menciones.map((m) => (m.id === mencionId ? { ...m, revision: r } : m)) }
        : d);
    } catch (e) {
      setErrorRevision(e instanceof Error ? e.message : String(e));
    }
  }

  async function revisarResolucion(resolucionId: number, decision: DecisionRevision): Promise<void> {
    setErrorRevision(null);
    try {
      await justiciaRevisarResolucion(cliente, resolucionId, decision);
      setDoc((d) => d
        ? {
          ...d,
          menciones: d.menciones.map((m) => ({
            ...m,
            resoluciones: m.resoluciones.map((r) => (r.id === resolucionId ? { ...r, estado: decision } : r)),
          })),
        }
        : d);
    } catch (e) {
      setErrorRevision(e instanceof Error ? e.message : String(e));
    }
  }

  async function revisarAfirmacion(afirmacionId: string, decision: DecisionRevision): Promise<void> {
    setErrorRevision(null);
    try {
      await justiciaRevisarAfirmacion(cliente, afirmacionId, decision);
      setDoc((d) => d
        ? { ...d, afirmaciones: d.afirmaciones.map((a) => (a.id === afirmacionId ? { ...a, estado: decision } : a)) }
        : d);
    } catch (e) {
      setErrorRevision(e instanceof Error ? e.message : String(e));
    }
  }

  async function vincularMencion(mencionId: number, entidadId: string): Promise<void> {
    setErrorRevision(null);
    try {
      const r = await visorVincularMencion(cliente, mencionId, entidadId);
      setDoc((d) => {
        if (!d) return d;
        return {
          ...d,
          menciones: d.menciones.map((m) => {
            if (m.id !== mencionId) return m;
            const yaExiste = m.resoluciones.some((x) => x.id === r.id);
            return { ...m, resoluciones: yaExiste ? m.resoluciones.map((x) => (x.id === r.id ? r : x)) : [...m.resoluciones, r] };
          }),
        };
      });
    } catch (e) {
      setErrorRevision(e instanceof Error ? e.message : String(e));
    }
  }

  async function crearMencion(tipo: string, rol: string | null): Promise<void> {
    if (!popover || popover.tipo !== "mencion") return;
    const sel = popover.sel;
    const r = await visorCrearMencion(cliente, {
      documento: id,
      fragmentId: sel.fragmentId,
      start: sel.start,
      len: sel.end - sel.start,
      tipo,
      rol,
    });
    setDoc((d) => d ? { ...d, menciones: [...d.menciones.filter((m) => m.id !== r.mencion.id), r.mencion] } : d);
    if (r.duplicada) mostrarToast("Ya existía una mención idéntica; se usó la existente.");
    setSeleccion({ tipo: "mencion", id: r.mencion.id });
    setTab("menciones");
    cerrarPopover();
  }

  async function crearAfirmacion(args: { subject: string; predicate: string; object: string; claimStatus: string }): Promise<void> {
    if (!popover || popover.tipo !== "afirmacion") return;
    const sel = popover.sel;
    const r = await visorCrearAfirmacion(cliente, {
      documento: id,
      fragmentId: sel.fragmentId,
      start: sel.start,
      len: sel.end - sel.start,
      subject: args.subject,
      predicate: args.predicate,
      object: args.object,
      claimStatus: args.claimStatus,
    });
    setDoc((d) => d ? { ...d, afirmaciones: [...d.afirmaciones.filter((a) => a.id !== r.id), r] } : d);
    if (r.duplicada) mostrarToast("Ya existía una afirmación idéntica; se usó la existente.");
    setSeleccion({ tipo: "afirmacion", id: r.id });
    setTab("afirmaciones");
    cerrarPopover();
  }

  const mencionesVisibles = useMemo(
    () => (doc ? (soloPendientes ? doc.menciones.filter((m) => m.revision === null) : doc.menciones) : []),
    [doc, soloPendientes],
  );
  const afirmacionesVisibles = useMemo(
    () => (doc ? (soloPendientes ? doc.afirmaciones.filter((a) => a.estado === "proposed") : doc.afirmaciones) : []),
    [doc, soloPendientes],
  );
  const filtrosResaltado: FiltrosVisor = useMemo(
    () => ({ tiposVisibles, estadosAfirmacionVisibles, verRechazadas }),
    [tiposVisibles, estadosAfirmacionVisibles, verRechazadas],
  );

  // Posición de un item en el documento: seq del fragmento del locator ×1e6
  // + offset dentro del fragmento. Los items sin fragmento (cargas de otra
  // corrida, p. ej. us_doj_news) van al final, ordenados por confianza.
  const posicionDe = useMemo(() => {
    const seqPorFragmento = new Map<string, number>();
    for (const f of doc?.fragmentos ?? []) seqPorFragmento.set(f.fragment_id, f.seq);
    return (locator: Record<string, unknown> | null): number => {
      const fragId = locator && typeof locator["fragment_id"] === "string" ? locator["fragment_id"] : null;
      const seq = fragId !== null ? seqPorFragmento.get(fragId) : undefined;
      if (seq === undefined) return Number.MAX_SAFE_INTEGER;
      const start = locator && typeof locator["start"] === "number" ? locator["start"] : 0;
      return seq * 1_000_000 + start;
    };
  }, [doc]);

  const pendientes = useMemo(() => {
    if (!doc) return [];
    const items: ({ tipo: "mencion"; data: MencionVisor } | { tipo: "afirmacion"; data: AfirmacionVisor })[] = [
      ...doc.menciones
        .filter((m) => m.revision === null && m.resoluciones.some((r) => r.estado === "proposed"))
        .map((m) => ({ tipo: "mencion" as const, data: m })),
      ...doc.afirmaciones
        .filter((a) => a.estado === "proposed")
        .map((a) => ({ tipo: "afirmacion" as const, data: a })),
    ];
    // Orden de lectura del documento, no de confianza: el revisor avanza
    // acompañando el texto de izquierda a derecha.
    items.sort((a, b) => {
      const pa = posicionDe(a.data.evidence_locator);
      const pb = posicionDe(b.data.evidence_locator);
      if (pa !== pb) return pa - pb;
      return (a.data.confidence ?? 0) - (b.data.confidence ?? 0);
    });
    return items;
  }, [doc, posicionDe]);

  const mencionesOrdenadas = useMemo(
    () => (doc ? [...doc.menciones].sort((a, b) => posicionDe(a.evidence_locator) - posicionDe(b.evidence_locator)) : []),
    [doc, posicionDe],
  );
  const afirmacionesOrdenadas = useMemo(
    () => (doc ? [...doc.afirmaciones].sort((a, b) => posicionDe(a.evidence_locator) - posicionDe(b.evidence_locator)) : []),
    [doc, posicionDe],
  );

  if (error) return <ErrorBox mensaje={error} />;
  if (doc === undefined) return <Cargando texto="Cargando el documento…" />;
  if (doc === null) return <ErrorBox mensaje="No existe un documento con ese identificador." />;

  return (
    <div>
      <a
        href={rutaAHash({ vista: "documento", id })}
        style={{ fontSize: 13, color: C.tiel, textDecoration: "none", fontWeight: 600 }}
      >
        ← {doc.documento.titulo}
      </a>

      <div style={{ ...st.tarjeta, marginTop: 10, marginBottom: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
          <div>
            <p style={st.eyebrow}>{doc.documento.autoridad} · {fecha(doc.documento.fecha)}</p>
            <h1 style={{ ...st.display, fontSize: 20, margin: "0 0 4px" }}>Revisar: {doc.documento.titulo}</h1>
          </div>
          {doc.version.snapshot ? (
            <BotonPdf cliente={cliente} documentoId={id} etiqueta="PDF original" onError={mostrarToast} />
          ) : null}
        </div>
        <p style={{ fontSize: 12.5, color: C.muted, margin: 0 }}>
          {String(doc.fragmentos.length)} fragmentos · {String(doc.menciones.length)} menciones · {String(doc.afirmaciones.length)} afirmaciones
        </p>
      </div>

      {errorRevision ? <ErrorBox mensaje={`No se pudo registrar la revisión: ${errorRevision}`} /> : null}

      {doc.fragmentos.length === 0 ? (
        <div style={st.tarjeta}>
          <p style={st.eyebrow}>Sin texto extraído</p>
          <p style={{ fontSize: 13, color: C.ink, lineHeight: 1.6 }}>
            Este documento todavía no tiene fragmentos cargados. Corre el loader sobre la corrida del extractor
            que lo produjo:
          </p>
          <pre style={{ background: C.grayBg, padding: 10, borderRadius: 8, fontSize: 12, overflowX: "auto" }}>
{`python -m app.scripts.nexo_documental.load_fragments \\
  --run-root <ruta-de-la-corrida> \\
  --supabase-url <url-del-proyecto> \\
  --key-file <archivo-con-service-role-key>`}
          </pre>
          {doc.documento.url ? (
            <a href={doc.documento.url} target="_blank" rel="noreferrer" style={{ color: C.tiel, fontSize: 12.5 }}>
              Documento original ↗
            </a>
          ) : null}
        </div>
      ) : (
        <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
          <div
            style={{
              flex: "1 1 62%",
              minWidth: 0,
              maxHeight: "calc(100vh - 150px)",
              overflowY: "auto",
              ...st.tarjeta,
            }}
            onMouseUp={manejarMouseUp}
          >
            <div
              style={{
                position: "sticky",
                top: -14,
                zIndex: 5,
                background: "#fff",
                padding: "6px 0 10px",
                marginBottom: 8,
                borderBottom: `1px solid ${C.line}`,
              }}
            >
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                <span style={{ fontSize: 10.5, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Menciones
                </span>
                {TIPOS_MENCION.map((t) => {
                  const activo = tiposVisibles.has(t);
                  const color = colorMencionTipo(t);
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => { alternarTipo(t); }}
                      style={{
                        ...st.btn,
                        padding: "3px 8px",
                        fontSize: 11.5,
                        borderColor: color,
                        color: activo ? color : C.muted,
                        opacity: activo ? 1 : 0.55,
                      }}
                    >
                      {MENCION_TIPO_ES[t] ?? t}
                    </button>
                  );
                })}
                <button
                  type="button"
                  onClick={() => { setVerRechazadas((v) => !v); }}
                  style={{ ...st.btn, padding: "3px 8px", fontSize: 11.5, fontWeight: verRechazadas ? 700 : 500 }}
                >
                  {verRechazadas ? "Ocultar rechazadas" : "Ver rechazadas"}
                </button>
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginTop: 6 }}>
                <span style={{ fontSize: 10.5, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Afirmaciones
                </span>
                {ESTADOS_AFIRMACION.map((e) => {
                  const activo = estadosAfirmacionVisibles.has(e);
                  const color = e === "approved" ? C.green : e === "rejected" ? C.muted : C.amber;
                  const etiqueta = e === "proposed" ? "Propuestas" : e === "approved" ? "Aprobadas" : "Rechazadas";
                  return (
                    <button
                      key={e}
                      type="button"
                      onClick={() => { alternarEstado(e); }}
                      style={{ ...st.btn, padding: "3px 8px", fontSize: 11.5, color: activo ? color : C.muted, opacity: activo ? 1 : 0.55 }}
                    >
                      {etiqueta}
                    </button>
                  );
                })}
                <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11.5, color: C.ink, marginLeft: 8 }}>
                  <input
                    type="checkbox"
                    checked={soloPendientes}
                    onChange={(e) => { setSoloPendientes(e.target.checked); }}
                  />
                  Solo pendientes
                </label>
              </div>
            </div>

            {doc.fragmentos.map((f, i) => {
              const anterior = i > 0 ? doc.fragmentos[i - 1] : undefined;
              const mostrarSeparador = f.page !== null && f.page !== (anterior?.page ?? null);
              return (
                <div key={f.fragment_id}>
                  {mostrarSeparador ? (
                    <div style={{ textAlign: "center", fontSize: 11, color: C.muted, margin: "16px 0", fontWeight: 700 }}>
                      — p. {String(f.page)} —
                    </div>
                  ) : null}
                  <TextoAnotado
                    fragmento={f}
                    menciones={mencionesVisibles}
                    afirmaciones={afirmacionesVisibles}
                    filtros={filtrosResaltado}
                    resaltar={seleccion}
                    onClickMencion={irACardMencion}
                    onClickAfirmacion={irACardAfirmacion}
                  />
                </div>
              );
            })}
          </div>

          <div style={{ flex: "1 1 38%", minWidth: 300, maxHeight: "calc(100vh - 150px)", overflowY: "auto" }}>
            <div style={{ display: "flex", gap: 4, marginBottom: 10, flexWrap: "wrap" }}>
              {(["pendientes", "menciones", "afirmaciones"] as const).map((t) => {
                const activo = tab === t;
                const n = t === "pendientes" ? pendientes.length : t === "menciones" ? doc.menciones.length : doc.afirmaciones.length;
                const etiqueta = t === "pendientes" ? "Pendientes" : t === "menciones" ? "Menciones" : "Afirmaciones";
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => { setTab(t); }}
                    style={{
                      ...st.btn,
                      background: activo ? C.tiel : "transparent",
                      color: activo ? "#fff" : C.ink,
                      borderColor: activo ? C.tiel : C.line,
                    }}
                  >
                    {etiqueta} ({String(n)})
                  </button>
                );
              })}
            </div>

            {tab === "pendientes" ? (
              pendientes.length === 0 ? (
                <p style={{ fontSize: 12.5, color: C.muted }}>Sin pendientes con estos filtros.</p>
              ) : (
                pendientes.map((item) => item.tipo === "mencion" ? (
                  <CardMencion
                    key={`m-${String(item.data.id)}`}
                    cliente={cliente}
                    mencion={item.data}
                    seleccionada={seleccion?.tipo === "mencion" && seleccion.id === item.data.id}
                    onVerTexto={() => { irATextoMencion(item.data); }}
                    onRevisar={revisarMencion}
                    onRevisarResolucion={revisarResolucion}
                    onVincular={vincularMencion}
                  />
                ) : (
                  <CardAfirmacion
                    key={`a-${item.data.id}`}
                    cliente={cliente}
                    documentoId={id}
                    tienePdf={doc.version.snapshot !== null}
                    afirmacion={item.data}
                    seleccionada={seleccion?.tipo === "afirmacion" && seleccion.id === item.data.id}
                    onVerTexto={() => { irATextoAfirmacion(item.data); }}
                    onRevisar={revisarAfirmacion}
                    onErrorPdf={mostrarToast}
                  />
                ))
              )
            ) : tab === "menciones" ? (
              doc.menciones.length === 0 ? <p style={{ fontSize: 12.5, color: C.muted }}>Sin menciones.</p> : mencionesOrdenadas.map((m) => (
                <CardMencion
                  key={m.id}
                  cliente={cliente}
                  mencion={m}
                  seleccionada={seleccion?.tipo === "mencion" && seleccion.id === m.id}
                  onVerTexto={() => { irATextoMencion(m); }}
                  onRevisar={revisarMencion}
                  onRevisarResolucion={revisarResolucion}
                  onVincular={vincularMencion}
                />
              ))
            ) : (
              doc.afirmaciones.length === 0 ? <p style={{ fontSize: 12.5, color: C.muted }}>Sin afirmaciones.</p> : afirmacionesOrdenadas.map((a) => (
                <CardAfirmacion
                  key={a.id}
                  cliente={cliente}
                  documentoId={id}
                  tienePdf={doc.version.snapshot !== null}
                  afirmacion={a}
                  seleccionada={seleccion?.tipo === "afirmacion" && seleccion.id === a.id}
                  onVerTexto={() => { irATextoAfirmacion(a); }}
                  onRevisar={revisarAfirmacion}
                  onErrorPdf={mostrarToast}
                />
              ))
            )}
          </div>
        </div>
      )}

      {toast ? (
        <div
          role="status"
          style={{
            position: "fixed",
            bottom: 20,
            left: "50%",
            transform: "translateX(-50%)",
            background: C.tielDark,
            color: "#fff",
            padding: "8px 16px",
            borderRadius: 8,
            fontSize: 12.5,
            zIndex: 80,
            boxShadow: "0 6px 18px rgba(0,0,0,0.25)",
          }}
        >
          {toast}
        </div>
      ) : null}

      {selActiva && !popover ? (
        <div
          style={{
            position: "fixed",
            top: selActiva.rect.bottom + 6,
            left: selActiva.rect.left,
            zIndex: 50,
            background: "#fff",
            border: `1px solid ${C.line}`,
            borderRadius: 8,
            boxShadow: "0 4px 14px rgba(0,0,0,0.16)",
            padding: 6,
            display: "flex",
            gap: 6,
          }}
        >
          <button type="button" style={st.btn} onClick={() => { setPopover({ tipo: "mencion", sel: selActiva }); }}>
            + Mención
          </button>
          <button type="button" style={st.btn} onClick={() => { setPopover({ tipo: "afirmacion", sel: selActiva }); }}>
            + Afirmación
          </button>
          <button
            type="button"
            style={{ ...st.btn, color: C.muted }}
            onClick={() => {
              setSelActiva(null);
              window.getSelection()?.removeAllRanges();
            }}
          >
            ×
          </button>
        </div>
      ) : null}

      {popover?.tipo === "mencion" ? (
        <PopoverMencion sel={popover.sel} onCancelar={cerrarPopover} onCrear={crearMencion} />
      ) : null}
      {popover?.tipo === "afirmacion" ? (
        <PopoverAfirmacion
          sel={popover.sel}
          menciones={doc.menciones}
          predicados={doc.predicados}
          onCancelar={cerrarPopover}
          onCrear={crearAfirmacion}
        />
      ) : null}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Cards del panel derecho
// ─────────────────────────────────────────────────────────────────────────────

function CardMencion({
  cliente,
  mencion,
  seleccionada,
  onVerTexto,
  onRevisar,
  onRevisarResolucion,
  onVincular,
}: {
  cliente: ClienteNexo;
  mencion: MencionVisor;
  seleccionada: boolean;
  onVerTexto: () => void;
  onRevisar: (id: number, decision: DecisionMencion) => Promise<void>;
  onRevisarResolucion: (id: number, decision: DecisionRevision) => Promise<void>;
  onVincular: (mencionId: number, entidadId: string) => Promise<void>;
}) {
  const rechazada = mencion.revision?.decision === "rejected";
  const confirmada = mencion.revision?.decision === "confirmed";
  return (
    <div
      id={`mencion-${String(mencion.id)}`}
      style={{
        ...st.tarjeta,
        padding: 10,
        marginBottom: 8,
        border: `1px solid ${seleccionada ? C.tiel : C.line}`,
        opacity: rechazada ? 0.65 : 1,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "flex-start" }}>
        <div>
          <span style={{ fontWeight: 700, fontSize: 13 }}>{mencion.mention_text}</span>
          <span style={{ fontSize: 11, color: colorMencionTipo(mencion.mention_type), fontWeight: 700 }}>
            {" "}· {MENCION_TIPO_ES[mencion.mention_type] ?? mencion.mention_type}
          </span>
          {mencion.proposed_role ? <span style={{ fontSize: 11, color: C.muted }}> · {mencion.proposed_role}</span> : null}
        </div>
        <button type="button" style={{ ...st.btn, padding: "2px 7px", fontSize: 11, flex: "none" }} onClick={onVerTexto}>
          Ver en texto
        </button>
      </div>
      <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
        confianza {mencion.confidence !== null ? mencion.confidence.toFixed(2) : "—"}
        {mencion.extractor_version ? ` · ${mencion.extractor_version}` : ""}
      </div>

      <div style={{ marginTop: 6 }}>
        {mencion.revision ? (
          <span style={{ fontSize: 11, fontWeight: 700, color: confirmada ? C.green : C.muted }}>
            {confirmada ? "confirmada" : "rechazada"}
          </span>
        ) : (
          <BotonesRevision
            aprobar="confirmed"
            rechazar="rejected"
            etiquetaAprobar="Confirmar"
            onDecidir={(d) => onRevisar(mencion.id, d)}
          />
        )}
      </div>

      {mencion.resoluciones.length > 0 ? (
        <div style={{ marginTop: 8, borderTop: `1px solid ${C.line}`, paddingTop: 6 }}>
          {mencion.resoluciones.map((r) => (
            <div key={r.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 11.5, padding: "3px 0", gap: 6 }}>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {r.caption ?? r.entidad_id}
                <span style={{ color: C.muted }}> · {schemaEs(r.schema)} · {r.metodo}{r.score !== null ? ` · ${r.score.toFixed(2)}` : ""}</span>
              </span>
              {r.estado === "proposed" ? (
                <BotonesRevision aprobar="approved" rechazar="rejected" onDecidir={(d) => onRevisarResolucion(r.id, d)} />
              ) : (
                <span style={{ color: r.estado === "approved" ? C.green : C.muted, fontWeight: 700, flex: "none" }}>
                  {r.estado === "approved" ? "aprobado" : "rechazado"}
                </span>
              )}
            </div>
          ))}
        </div>
      ) : null}

      <BuscadorVincular cliente={cliente} onVincular={(entidadId) => onVincular(mencion.id, entidadId)} />
    </div>
  );
}

function CardAfirmacion({
  cliente,
  documentoId,
  tienePdf,
  afirmacion,
  seleccionada,
  onVerTexto,
  onRevisar,
  onErrorPdf,
}: {
  cliente: ClienteNexo;
  documentoId: string;
  /** true si el documento tiene snapshot PDF (0107) — sin esto, "PDF p.N" no se muestra aunque haya page. */
  tienePdf: boolean;
  afirmacion: AfirmacionVisor;
  seleccionada: boolean;
  onVerTexto: () => void;
  onRevisar: (id: string, decision: DecisionRevision) => Promise<void>;
  onErrorPdf: (msg: string) => void;
}) {
  const loc = afirmacion.evidence_locator;
  const claimStatus = loc && typeof loc["claim_status"] === "string" ? loc["claim_status"] : null;
  const manual = loc?.["manual"] === true;
  const realineada = loc?.["evidence_realigned"] === true;
  const tieneOffsets = !!loc && typeof loc["start"] === "number" && typeof loc["end"] === "number"
    && typeof loc["fragment_id"] === "string";
  const page = loc && typeof loc["page"] === "number" ? loc["page"] : null;

  return (
    <div
      id={`afirmacion-${afirmacion.id}`}
      style={{ ...st.tarjeta, padding: 10, marginBottom: 8, border: `1px solid ${seleccionada ? C.tiel : C.line}` }}
    >
      <div style={{ fontWeight: 600, fontSize: 12.5 }}>
        {afirmacion.subject_text ?? "quién: por determinar"}{" "}
        <span style={{ color: C.tiel }}>{afirmacion.predicate.replace(/_/g, " ")}</span>{" "}
        {afirmacion.object_text ?? "sobre quién o qué: por determinar"}
      </div>
      <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
        {POSTURA_ES[afirmacion.postura] ?? afirmacion.postura}
        {claimStatus ? ` · ${CLAIM_STATUS_ES[claimStatus] ?? claimStatus}` : ""}
        {" · confianza "}{afirmacion.confidence !== null ? afirmacion.confidence.toFixed(2) : "—"}
      </div>
      <blockquote
        style={{
          margin: "8px 0 6px",
          padding: "7px 10px",
          borderLeft: `3px solid ${C.tiel}`,
          background: C.tielSoft,
          fontSize: 12,
          lineHeight: 1.5,
          color: C.ink,
        }}
      >
        “{afirmacion.evidence_excerpt}”
      </blockquote>
      {manual || realineada ? (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
          {manual ? <Insignia texto="manual" color={C.blue} fondo={C.blueBg} /> : null}
          {realineada ? <Insignia texto="evidencia realineada" color={C.amber} fondo={C.amberBg} /> : null}
        </div>
      ) : null}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        {afirmacion.estado === "proposed" ? (
          <BotonesRevision aprobar="approved" rechazar="rejected" onDecidir={(d) => onRevisar(afirmacion.id, d)} />
        ) : (
          <span style={{ fontSize: 11, fontWeight: 700, color: afirmacion.estado === "approved" ? C.green : C.muted }}>
            {afirmacion.estado === "approved" ? "aprobada" : "rechazada"}
          </span>
        )}
        <div style={{ display: "flex", gap: 6 }}>
          {tienePdf && page !== null ? (
            <BotonPdf cliente={cliente} documentoId={documentoId} page={page} etiqueta={`PDF p.${String(page)}`} onError={onErrorPdf} />
          ) : null}
          {tieneOffsets ? (
            <button type="button" style={{ ...st.btn, padding: "2px 7px", fontSize: 11 }} onClick={onVerTexto}>
              Ver en texto
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** Abre el PDF original en una pestaña nueva, en la página indicada (default
 * 1). Firma la URL en cada click (nunca se cachea entre botones — la Edge
 * Function la expira a los 600s, más simple que gestionar su vigencia aquí). */
function BotonPdf({
  cliente,
  documentoId,
  page,
  etiqueta,
  onError,
}: {
  cliente: ClienteNexo;
  documentoId: string;
  page?: number | null;
  etiqueta: string;
  onError: (msg: string) => void;
}) {
  const [cargando, setCargando] = useState(false);

  async function abrir(): Promise<void> {
    setCargando(true);
    try {
      const url = await visorSnapshotUrl(cliente, documentoId);
      window.open(`${url}#page=${String(page ?? 1)}`, "_blank", "noopener,noreferrer");
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setCargando(false);
    }
  }

  return (
    <button
      type="button"
      style={{ ...st.btn, padding: "2px 7px", fontSize: 11 }}
      disabled={cargando}
      onClick={() => { void abrir(); }}
    >
      {cargando ? "Abriendo…" : etiqueta}
    </button>
  );
}

function Insignia({ texto, color, fondo }: { texto: string; color: string; fondo: string }) {
  return (
    <span
      style={{
        display: "inline-block",
        padding: "1px 7px",
        borderRadius: 999,
        fontSize: 10.5,
        fontWeight: 700,
        color,
        background: fondo,
      }}
    >
      {texto}
    </span>
  );
}

/** Buscador de entidades para vincular una mención (debounce 300ms, patrón de Explorador/Justicia). */
function BuscadorVincular({ cliente, onVincular }: { cliente: ClienteNexo; onVincular: (entidadId: string) => Promise<void> }) {
  const [q, setQ] = useState("");
  const [resultados, setResultados] = useState<FilaEntidad[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [vinculando, setVinculando] = useState<string | null>(null);

  useEffect(() => {
    const texto = q.trim();
    if (texto.length < 2) {
      setResultados([]);
      return;
    }
    let vivo = true;
    setBuscando(true);
    const timer = window.setTimeout(() => {
      vizEntidades(cliente, { q: texto, limit: 10 })
        .then((r) => {
          if (vivo) {
            setResultados(r.filas);
            setBuscando(false);
          }
        })
        .catch(() => {
          if (vivo) setBuscando(false);
        });
    }, 300);
    return () => {
      vivo = false;
      window.clearTimeout(timer);
    };
  }, [cliente, q]);

  return (
    <div style={{ marginTop: 8, borderTop: `1px solid ${C.line}`, paddingTop: 6 }}>
      <input
        style={{ ...st.input, width: "100%", fontSize: 12, padding: "6px 9px" }}
        placeholder="Vincular a entidad…"
        value={q}
        onChange={(e) => { setQ(e.target.value); }}
      />
      {buscando ? <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>Buscando…</div> : null}
      {resultados.length > 0 ? (
        <div style={{ marginTop: 4, border: `1px solid ${C.line}`, borderRadius: 8, overflow: "hidden" }}>
          {resultados.map((r) => (
            <button
              key={r.id}
              type="button"
              disabled={vinculando !== null}
              onClick={() => {
                setVinculando(r.id);
                void onVincular(r.id).finally(() => {
                  setVinculando(null);
                  setQ("");
                  setResultados([]);
                });
              }}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                padding: "6px 8px",
                fontSize: 11.5,
                border: "none",
                borderBottom: `1px solid ${C.line}`,
                background: "#fff",
                cursor: "pointer",
              }}
            >
              <strong>{r.caption ?? r.id}</strong>
              <span style={{ color: C.muted }}> · {schemaEs(r.schema)}{r.pais ? ` · ${r.pais}` : ""}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Popovers de creación manual (selección de texto → mención / afirmación)
// ─────────────────────────────────────────────────────────────────────────────

function PopoverMencion({
  sel,
  onCancelar,
  onCrear,
}: {
  sel: SeleccionAnotada;
  onCancelar: () => void;
  onCrear: (tipo: string, rol: string | null) => Promise<void>;
}) {
  const [tipo, setTipo] = useState(TIPOS_MENCION[0] ?? "person");
  const [rol, setRol] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function crear(): Promise<void> {
    setGuardando(true);
    setError(null);
    try {
      await onCrear(tipo, rol.trim() || null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setGuardando(false);
    }
  }

  return (
    <div
      style={{
        position: "fixed",
        top: sel.rect.bottom + 6,
        left: sel.rect.left,
        zIndex: 60,
        background: "#fff",
        border: `1px solid ${C.line}`,
        borderRadius: 10,
        boxShadow: "0 8px 22px rgba(0,0,0,0.18)",
        padding: 12,
        width: 260,
      }}
    >
      <p style={{ ...st.eyebrow, margin: "0 0 8px" }}>Nueva mención</p>
      <p style={{ fontSize: 12, color: C.muted, margin: "0 0 8px", lineHeight: 1.4 }}>“{sel.texto}”</p>
      <label style={st.label} htmlFor="nm-tipo">Tipo</label>
      <select id="nm-tipo" style={{ ...st.input, width: "100%", marginBottom: 8 }} value={tipo} onChange={(e) => { setTipo(e.target.value); }}>
        {TIPOS_MENCION.map((t) => <option key={t} value={t}>{MENCION_TIPO_ES[t] ?? t}</option>)}
      </select>
      <label style={st.label} htmlFor="nm-rol">Rol (opcional)</label>
      <input
        id="nm-rol"
        style={{ ...st.input, width: "100%", marginBottom: 8 }}
        value={rol}
        onChange={(e) => { setRol(e.target.value); }}
        placeholder="p. ej. testigo"
      />
      {error ? <div style={{ color: C.red, fontSize: 11.5, marginBottom: 8 }}>{error}</div> : null}
      <div style={{ display: "flex", gap: 8 }}>
        <button type="button" style={st.btnPrim} disabled={guardando} onClick={() => { void crear(); }}>
          {guardando ? "Creando…" : "Crear mención"}
        </button>
        <button type="button" style={st.btn} disabled={guardando} onClick={onCancelar}>Cancelar</button>
      </div>
    </div>
  );
}

function PopoverAfirmacion({
  sel,
  menciones,
  predicados,
  onCancelar,
  onCrear,
}: {
  sel: SeleccionAnotada;
  menciones: MencionVisor[];
  predicados: string[];
  onCancelar: () => void;
  onCrear: (args: { subject: string; predicate: string; object: string; claimStatus: string }) => Promise<void>;
}) {
  const [subject, setSubject] = useState(sel.texto);
  const [predicate, setPredicate] = useState("");
  const [object, setObject] = useState(sel.texto);
  const [claimStatus, setClaimStatus] = useState<string>("mentioned");
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mencionesUnicas = useMemo(() => [...new Set(menciones.map((m) => m.mention_text))], [menciones]);

  async function crear(): Promise<void> {
    if (!predicate) {
      setError("Selecciona un predicado.");
      return;
    }
    if (!subject.trim() || !object.trim()) {
      setError("Indica quién y sobre quién o qué.");
      return;
    }
    setGuardando(true);
    setError(null);
    try {
      await onCrear({ subject: subject.trim(), predicate, object: object.trim(), claimStatus });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setGuardando(false);
    }
  }

  return (
    <div
      style={{
        position: "fixed",
        top: sel.rect.bottom + 6,
        left: Math.max(8, sel.rect.left - 30),
        zIndex: 60,
        background: "#fff",
        border: `1px solid ${C.line}`,
        borderRadius: 10,
        boxShadow: "0 8px 22px rgba(0,0,0,0.18)",
        padding: 12,
        width: 320,
        maxHeight: "72vh",
        overflowY: "auto",
      }}
    >
      <p style={{ ...st.eyebrow, margin: "0 0 8px" }}>Nueva afirmación</p>
      <p style={{ fontSize: 12, color: C.muted, margin: "0 0 8px", lineHeight: 1.4 }}>Evidencia: “{sel.texto}”</p>

      <label style={st.label} htmlFor="na-subject">Quién</label>
      <input id="na-subject" style={{ ...st.input, width: "100%", marginBottom: 4 }} value={subject} onChange={(e) => { setSubject(e.target.value); }} />
      {mencionesUnicas.length > 0 ? (
        <select
          style={{ ...st.input, width: "100%", marginBottom: 8, fontSize: 11.5 }}
          value=""
          onChange={(e) => { if (e.target.value) setSubject(e.target.value); }}
        >
          <option value="">usar mención…</option>
          {mencionesUnicas.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      ) : null}

      <label style={st.label} htmlFor="na-predicate">Qué afirma</label>
      <select id="na-predicate" style={{ ...st.input, width: "100%", marginBottom: 8 }} value={predicate} onChange={(e) => { setPredicate(e.target.value); }}>
        <option value="">Selecciona…</option>
        {predicados.map((p) => <option key={p} value={p}>{p.replace(/_/g, " ")}</option>)}
      </select>

      <label style={st.label} htmlFor="na-object">Sobre quién o qué</label>
      <input id="na-object" style={{ ...st.input, width: "100%", marginBottom: 4 }} value={object} onChange={(e) => { setObject(e.target.value); }} />
      {mencionesUnicas.length > 0 ? (
        <select
          style={{ ...st.input, width: "100%", marginBottom: 8, fontSize: 11.5 }}
          value=""
          onChange={(e) => { if (e.target.value) setObject(e.target.value); }}
        >
          <option value="">usar mención…</option>
          {mencionesUnicas.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      ) : null}

      <label style={st.label} htmlFor="na-claim">Estado probatorio</label>
      <select id="na-claim" style={{ ...st.input, width: "100%", marginBottom: 8 }} value={claimStatus} onChange={(e) => { setClaimStatus(e.target.value); }}>
        {CLAIM_STATUS_CREACION.map((c) => <option key={c} value={c}>{CLAIM_STATUS_ES[c] ?? c}</option>)}
      </select>

      {error ? <div style={{ color: C.red, fontSize: 11.5, marginBottom: 8 }}>{error}</div> : null}
      <div style={{ display: "flex", gap: 8 }}>
        <button type="button" style={st.btnPrim} disabled={guardando} onClick={() => { void crear(); }}>
          {guardando ? "Creando…" : "Crear afirmación"}
        </button>
        <button type="button" style={st.btn} disabled={guardando} onClick={onCancelar}>Cancelar</button>
      </div>
    </div>
  );
}
